/**
 *
 * @Description: NMC Tooltip (base on rc-tooltip: https://github.com/react-component/tooltip)
 *
 */

'use strict'

import React, {Component} from 'react'
import PropTypes from 'prop-types'
import RCTooltip from 'rc-tooltip'
import ReactDOM from 'react-dom'

import './Tooltip.less'

const NMC_TOOLTIP_CLASS = 'nmc-tooltip'
const ARROW_PICK_OFFSET = 19 // offset from edge to the pick of caret (arrow)
const ARROW_START_OFFSET = 10 // according to the style guide always 10px offset from edge for the left/right top/bottom caret(arrow) location
const ARROW_HEIGHT = 9 // according to style guide
const TOOLTIP_TARGET_REF = '_nmcTooltipTargetRef'

class Tooltip extends Component {

  static TooltipPlacement = {
    LEFT: 'left',
    RIGHT: 'right',
    TOP: 'top',
    BOTTOM: 'bottom',
    TOP_LEFT: 'topLeft',
    LEFT_TOP: 'leftTop',
    TOP_RIGHT: 'topRight',
    RIGHT_TOP: 'rightTop',
    BOTTOM_RIGHT: 'bottomRight',
    RIGHT_BOTTOM: 'rightBottom',
    BOTTOM_LEFT: 'bottomLeft',
    LEFT_BOTTOM: 'leftBottom'
  }

  static TooltipTrigger = {

    HOVER: 'hover',
    CLICK: 'click'
  }

  constructor (props) {
    super(props)
    this.state = {
      align: {offset: [0, 0], targetOffset: [0, 0]},
      visible: false
    }
  }

  componentWillMount () {
    if (this.props.closeOnTooltipClick) {
      // handle to hide tooltip 'on click outside' of tooltip component
      window.document.addEventListener('click', this.handleClickOutside)
    }
  }

  componentWillUnmount () {
    if (this.props.closeOnTooltipClick) {
      window.document.removeEventListener('click', this.handleClickOutside)
    }
  }

  handleClickOutside = (event) => {
    let overlayElement = ReactDOM.findDOMNode(this.refs[TOOLTIP_TARGET_REF])
    if (overlayElement && (event.target !== overlayElement.firstChild) && (event.target !== overlayElement)) {
      // hide tooltip when click outside of tooltip target
      this.setState({visible: false})
    }
  }

  addPropsToChildren = () => {
    let {closeOnTooltipClick} = this.props

    let handleOpenTooltipManually = this.handleOpenTooltipManually

    let child = React.Children.only(this.props.children)

    // we add ref: '_nmcTooltipTargetRef' to find the overlay in order to align tooltip properly around tooltip target
    let extraProps = {
      ref: TOOLTIP_TARGET_REF
    }
    if (closeOnTooltipClick) {
      extraProps.onClick = handleOpenTooltipManually
    }
    return React.cloneElement(child, extraProps)
  }

  handleOpenTooltipManually = (event) => {
    this.setState({visible: !this.state.visible})
  }

  handleHideTooltipManually = () => {
    this.setState({visible: false})
  }

  componentDidMount () {
    // align tooltip properly around tooltip target according to the style guide
    const {placement} = this.props
    const targetChildNode = ReactDOM.findDOMNode(this.refs[TOOLTIP_TARGET_REF])
    if (targetChildNode) {
      const targetChildNodeRect = targetChildNode.getBoundingClientRect()
      if (targetChildNodeRect) {
        let childrenW = targetChildNodeRect.width
        let childrenH = targetChildNodeRect.height
        let offset = [0, 0]
        let targetOffset = [0, 0]
        switch (placement) {
          case Tooltip.TooltipPlacement.TOP:
            targetOffset = [0, ARROW_HEIGHT]
            offset = [0, -1]
            break
          case Tooltip.TooltipPlacement.TOP_LEFT:
            targetOffset = [childrenW <= (ARROW_PICK_OFFSET + ARROW_START_OFFSET) ? (ARROW_PICK_OFFSET - childrenW / 2) : 0, ARROW_HEIGHT]
            offset = [-2, -1]
            break
          case Tooltip.TooltipPlacement.TOP_RIGHT:
            targetOffset = [childrenW <= (ARROW_PICK_OFFSET + ARROW_START_OFFSET) ? -(ARROW_PICK_OFFSET - childrenW / 2) : 0, ARROW_HEIGHT]
            offset = [2, -1]
            break
          case Tooltip.TooltipPlacement.LEFT:
            targetOffset = [ARROW_HEIGHT, 0]
            offset = [0, -1]
            break
          case Tooltip.TooltipPlacement.LEFT_TOP:
            targetOffset = [ARROW_HEIGHT, childrenH <= (ARROW_PICK_OFFSET + ARROW_START_OFFSET) ? (ARROW_PICK_OFFSET - childrenH / 2) : 0]
            offset = [0, -2]
            break
          case Tooltip.TooltipPlacement.LEFT_BOTTOM:
            targetOffset = [ARROW_HEIGHT, childrenH <= (ARROW_PICK_OFFSET + ARROW_START_OFFSET) ? -(ARROW_PICK_OFFSET - childrenH / 2) : 0]
            offset = [0, 2]
            break
          case Tooltip.TooltipPlacement.BOTTOM_LEFT:
            targetOffset = [childrenW <= (ARROW_PICK_OFFSET + ARROW_START_OFFSET) ? (ARROW_PICK_OFFSET - childrenW / 2) : 0, -ARROW_HEIGHT]
            offset = [-2, -1]
            break
          case Tooltip.TooltipPlacement.BOTTOM_RIGHT:
            targetOffset = [childrenW <= (ARROW_PICK_OFFSET + ARROW_START_OFFSET) ? -(ARROW_PICK_OFFSET - childrenW / 2) : 0, -ARROW_HEIGHT]
            offset = [2, -1]
            break
          case Tooltip.TooltipPlacement.RIGHT:
            targetOffset = [-ARROW_HEIGHT, 0]
            break
          case Tooltip.TooltipPlacement.RIGHT_TOP:
            targetOffset = [-ARROW_HEIGHT, childrenH <= (ARROW_PICK_OFFSET + ARROW_START_OFFSET) ? ARROW_PICK_OFFSET - childrenH / 2 : 0]
            offset = [0, -2]
            break
          case Tooltip.TooltipPlacement.RIGHT_BOTTOM:
            targetOffset = [-ARROW_HEIGHT, childrenH <= (ARROW_PICK_OFFSET + ARROW_START_OFFSET) ? -(ARROW_PICK_OFFSET - childrenH / 2) : 0]
            offset = [0, 2]
            break
          default: // Tooltip.TooltipPlacement.BOTTOM
            targetOffset = [0, -ARROW_HEIGHT]
            offset = [0, -1]
            break
        }

        this.setState({align: {offset: offset, targetOffset: targetOffset}})
      }
    }
  }

  addCloseOnTooltipHandle = (tooltipComponent) => {
    // handle case when we want to hide the tooltip on lick inside tooltip component
    let me = this
    let {closeOnTooltipClick} = this.props
    let {children} = tooltipComponent.props
    if (closeOnTooltipClick && (typeof children !== 'string')) {
      if (children.map) {
        return children.map((child) => {
          let onClickOrig = child.props.onClick
          let newProps = Object.assign({}, child.props, {
            onClick: (event) => {
              if (onClickOrig) {
                // call to original click, if exists
                onClickOrig(event)
              }
              // than call to hide tooltip
              me.handleHideTooltipManually()
            }
          })
          return Object.assign({}, child, {props: newProps})
        })
      } else {
        return tooltipComponent
      }
    } else {
      return tooltipComponent
    }
  }

  render () {
    let {tooltipComponent, closeOnTooltipClick} = this.props
    if (typeof tooltipComponent === 'string') {
      let props = closeOnTooltipClick ? {onClick: this.handleHideTooltipManually} : {}
      tooltipComponent = React.createElement('div', props, tooltipComponent)
    }

    let newProps = Object.assign({}, this.props, {
      children: this.addPropsToChildren(),
      overlay: this.addCloseOnTooltipHandle(tooltipComponent)
    })

    if (closeOnTooltipClick) {
      // in case 'closeOnTooltipClick' is on, we enforce tooltip trigger to be 'click' and handle tooltip visibility manually
      newProps = Object.assign({}, newProps, {
        visible: this.state.visible,
        trigger: [Tooltip.TooltipTrigger.CLICK]
      })
    }

    return (
      <RCTooltip {...newProps} align={this.state.align} />
    )
  }
}

Tooltip.propTypes = {
  placement: PropTypes.oneOf([Tooltip.TooltipPlacement.LEFT, Tooltip.TooltipPlacement.LEFT_BOTTOM, Tooltip.TooltipPlacement.LEFT_TOP, Tooltip.TooltipPlacement.RIGHT, Tooltip.TooltipPlacement.RIGHT_TOP, Tooltip.TooltipPlacement.RIGHT_BOTTOM, Tooltip.TooltipPlacement.TOP, Tooltip.TooltipPlacement.TOP_RIGHT, Tooltip.TooltipPlacement.TOP_LEFT, Tooltip.TooltipPlacement.BOTTOM, Tooltip.TooltipPlacement.BOTTOM_LEFT, Tooltip.TooltipPlacement.BOTTOM_RIGHT]),
  trigger: PropTypes.arrayOf(PropTypes.oneOf([Tooltip.TooltipTrigger.HOVER, Tooltip.TooltipTrigger.CLICK])),
  tooltipComponent: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.func
  ]).isRequired,
  closeOnTooltipClick: PropTypes.bool
}

Tooltip.defaultProps = {
  placement: Tooltip.TooltipPlacement.BOTTOM,
  trigger: [Tooltip.TooltipTrigger.HOVER],
  prefixCls: NMC_TOOLTIP_CLASS,
  closeOnTooltipClick: false
}

export {
  Tooltip
}




// WEBPACK FOOTER //
// ../~/@master/webinfra-frontend-components/src/components/Tooltip/index.js